import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { SponsorProject } from "../../hooks";
import { isNotionProjectUrl } from "./notionProjectUrl";

interface SponsorProjectSelectProps {
  id: string;
  /** 選択部品の見出しになる要素の id。選択中のプロジェクト名と合わせて読み上げる */
  labelId: string;
  sponsorProjects: SponsorProject[];
  value: string | null;
  onChange: (sponsorProject: SponsorProject) => void;
}

/**
 * スポンサーのプロジェクトを1つ選ぶ、検索つきの選択部品。
 * 詳細URLに Notion の URL が登録されていないプロジェクトは、一覧に出すが選べない。
 */
export function SponsorProjectSelect({
  id,
  labelId,
  sponsorProjects,
  value,
  onChange,
}: SponsorProjectSelectProps) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const isComposingRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const selected = sponsorProjects.find(
    (sponsorProject) => sponsorProject.sponsorProjectId === value,
  );
  const normalizedKeyword = keyword.trim().toLowerCase();
  const filtered = sponsorProjects.filter((sponsorProject) =>
    sponsorProject.name.toLowerCase().includes(normalizedKeyword),
  );

  const handleSelect = (sponsorProject: SponsorProject) => {
    onChange(sponsorProject);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-labelledby={`${labelId} ${id}-value`}
        aria-expanded={isOpen}
        onClick={() => {
          setKeyword("");
          setIsOpen((prev) => !prev);
        }}
        className="box-border flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-[var(--color-border-default)] bg-white px-3 py-2.5 text-left text-sm outline-none focus:border-[var(--color-primary-500)]"
      >
        <span
          id={`${id}-value`}
          className={`min-w-0 flex-1 truncate ${selected ? "text-[var(--color-text-primary)]" : "text-[var(--color-text-tertiary)]"}`}
        >
          {selected ? selected.name : "プロジェクトを選択"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 left-0 z-10 mt-1 rounded-md border border-[var(--color-border-default)] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-2 border-[var(--color-border-default)] border-b px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
            <input
              type="text"
              // biome-ignore lint/a11y/noAutofocus: 開いた直後にプロジェクト名で絞り込めるようにする
              autoFocus
              aria-label="プロジェクト名で検索"
              aria-controls={listboxId}
              placeholder="プロジェクト名で検索"
              defaultValue=""
              onChange={(e) => {
                // IME の変換中は、確定前の読みで絞り込まない
                if (isComposingRef.current) return;
                setKeyword(e.target.value);
              }}
              onCompositionStart={() => {
                isComposingRef.current = true;
              }}
              onCompositionEnd={(e) => {
                isComposingRef.current = false;
                setKeyword(e.currentTarget.value);
              }}
              onKeyDown={(e) => {
                // 変換を確定・取り消すキーで、一覧を閉じたりフォームを送信したりしない
                if (e.nativeEvent.isComposing) return;
                if (e.key === "Escape") setIsOpen(false);
                // 検索欄の Enter で、タームの作成フォームが送信されないようにする
                if (e.key === "Enter") e.preventDefault();
              }}
              className="box-border w-full border-none text-sm outline-none"
            />
          </div>
          <div
            id={listboxId}
            role="listbox"
            aria-label="プロジェクト"
            className="max-h-60 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-[var(--color-text-secondary)] text-sm">
                一致するプロジェクトがありません
              </p>
            ) : (
              filtered.map((sponsorProject) => {
                const isSelectable = isNotionProjectUrl(
                  sponsorProject.projectUrl,
                );
                const unselectableReason = sponsorProject.projectUrl
                  ? "詳細URLがNotionのURLではありません"
                  : "詳細URLが登録されていません";
                const isSelected = sponsorProject.sponsorProjectId === value;
                return (
                  <button
                    key={sponsorProject.sponsorProjectId}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={!isSelectable}
                    onClick={() => handleSelect(sponsorProject)}
                    className={`flex w-full items-start gap-2 border-none bg-white px-3 py-2 text-left text-sm ${
                      isSelectable
                        ? "cursor-pointer text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle-gray)]"
                        : "cursor-not-allowed text-[var(--color-text-tertiary)]"
                    }`}
                  >
                    <Check
                      className={`mt-0.5 h-4 w-4 shrink-0 text-[var(--color-primary-500)] ${isSelected ? "" : "invisible"}`}
                    />
                    <span className="min-w-0 flex-1 break-all">
                      {sponsorProject.name}
                      {!isSelectable && (
                        <span className="block text-xs">
                          {unselectableReason}
                        </span>
                      )}
                    </span>
                    {sponsorProject.isCompleted && (
                      <span className="shrink-0 whitespace-nowrap rounded-full bg-[var(--color-bg-subtle-gray)] px-2 py-0.5 text-[12px] text-[var(--color-text-secondary)]">
                        終了
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
